import { getVal, isLoaded } from 'react-redux-firebase'
import { createSelector } from 'reselect' //, createSelectorCreator, defaultMemoize } from 'reselect'
import Rectangle from '../../utils/Rectangle'
import { cols, smallScreen, defaultBlockLimit, snapThreshold } from 'variables'
import {
  currentBoardIdSelector,
  placementBlockSelector,
  placementWidthSelector,
  embedModeSelector,
  currentBoardSelector,
  editModeSelector,
  currentBlockSelector,
  modeSelector
} from './selectors'
import { getSnapper } from './snapping'
import { sidebarShowingSelector } from './sidebarSelectors'
import { rowHeight, sidebarWidth } from '../../style'
import { firebaseSelector } from './baseSelectors'
import clamp from 'lodash/clamp'

const emptyObject = {}

// const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual)

// const debugCreateSelector = label => (inputs, func) => {
//   const wrapper = (...args) => {
//     console.log(`  Run ${label}`);
//     return func(...args)
//   }
//   const selector = createSelector(inputs, wrapper)
//   return state => {
//     console.log(`Call ${label}?`);
//     return selector(state)
//   }
// }

export const sizeSelector = state => state.board.size

export const screenSizeSelector = createSelector(
  [sizeSelector],
  size => {
    if (size.width <= smallScreen) size.sizeClass = 'small'
    return size
  }
)
export const smallScreenSelector = createSelector(
  [sizeSelector],
  size => {
    return size.width <= smallScreen
  }
)
export const boardWidthSelector = createSelector(
  [screenSizeSelector, sidebarShowingSelector, embedModeSelector],
  ({ width, sizeClass }, globalNav, embed) => {
    if (globalNav && !embed && sizeClass !== 'small') width -= sidebarWidth
    return width
  }
)
export const boardSizeSelector = createSelector(
  [screenSizeSelector, sidebarShowingSelector, modeSelector, embedModeSelector],
  ({ width, sizeClass, height }, globalNav, mode, embed) => {
    const size = { width, height }
    if (globalNav && !embed && sizeClass !== 'small') size.width -= sidebarWidth
    if (!embed) size.height -= rowHeight.default
    if (mode === 'discuss') size.height -= rowHeight.default
    return size
  }
)

export const getToolsCanHide = createSelector(
  [currentBoardIdSelector, modeSelector, sidebarShowingSelector],
  (boardId, mode, sidebar) => boardId && mode === 'view' && !sidebar
)

const currentBoardOwnerSelector = createSelector(
  [currentBoardSelector],
  ({ owner }) => owner
)

const ownerBlockCountSelector = createSelector(
  [currentBoardOwnerSelector, firebaseSelector],
  (owner, firebase) => getVal(firebase, `users/${owner.id}/blockCount`)
)
const ownerBlockLimitSelector = createSelector(
  [currentBoardOwnerSelector, firebaseSelector],
  (owner, firebase) =>
    getVal(firebase, `users/${owner.id}/blockLimit`) || defaultBlockLimit
)
export const boardFullSelector = createSelector(
  [ownerBlockCountSelector, ownerBlockLimitSelector],
  (blockCount, blockLimit) => blockCount >= blockLimit
)

export const colWidthSelector = createSelector(
  [boardWidthSelector],
  width => width / cols
)

export const blocksSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector],
  (firebase, boardId) => getVal(firebase, `blocks/${boardId}`)
)

export const sectionsSelector = createSelector(
  [firebaseSelector, currentBoardIdSelector],
  (firebase, boardId) => getVal(firebase, `sections/${boardId}`) || []
)
export const orderedSectionsSelector = createSelector(
  [sectionsSelector],
  sections => Object.values(sections).sort((a, b) => a.number - b.number)
)

export const _selectedIdsSelector = state => state.selectedBlocks.selected
// get blocks from array of selected block and group ids
export const selectedBlocksSelector = createSelector(
  [_selectedIdsSelector, blocksSelector],
  (ids, blocks) => {
    const selectedBlocks = []
    if (!blocks) return []
    ids.forEach(id => {
      if (id in blocks) {
        selectedBlocks.push(blocks[id])
      } else {
        const groupBlocks = Object.values(blocks).filter(
          block => block.group === id
        )
        selectedBlocks.push(...groupBlocks)
      }
    })
    return selectedBlocks
  }
)

export const cropBlockIdSelector = state =>
  editModeSelector(state) && state.selectedBlocks.cropBlock
export const cropBlockSelector = createSelector(
  [cropBlockIdSelector, blocksSelector],
  (id, blocks) => id && blocks && blocks[id]
)

export const rawDragOffsetSelector = createSelector(
  [state => state.selectedBlocks.dragOffset],
  dragOffset => {
    if (!dragOffset) return
    if (!dragOffset.x && !dragOffset.y) return

    return dragOffset
  }
)
export const dragOffsetSelector = createSelector(
  [rawDragOffsetSelector, colWidthSelector],
  (dragOffset, colWidth) => {
    if (!dragOffset) return

    let x = dragOffset.x / colWidth
    let y = dragOffset.y / colWidth
    return { ...dragOffset, x, y }
  }
)

export const placementPositionSelector = state => state.board.placementPosition

/**
 * Based on blocks in section
 * only certain changes to blocks will result in a changed output
 * TODO check equality where used
 */
export const _sectionMinHeightsSelector = createSelector(
  [blocksSelector],
  blocks => {
    if (!blocks) return emptyObject
    let sections = {}
    Object.values(blocks || {}).forEach(block => {
      const bottom =
        (block.aspect ? block.aspect * block.width : block.height) + block.top
      if (block.section in sections) {
        sections[block.section] = Math.max(sections[block.section], bottom)
      } else {
        sections[block.section] = bottom
      }
    })
    return sections
  }
)

/**
 * selects Map of sections with min-height and top (offset from top of board)
 */
export const sectionLayoutSelector = createSelector(
  [_sectionMinHeightsSelector, orderedSectionsSelector],
  (sectionMinHeights, sections) => {
    if (!sectionMinHeights || !sections) return
    let top = 0
    const layout = new Map(
      sections.map((section, i) => {
        let minHeight = sectionMinHeights[section.id] || 0

        // Snap to 1 col away from min height
        let height = section.height
        if (Math.abs(height - 1 - minHeight) < snapThreshold)
          height = minHeight + 1

        const entry = [
          section.id,
          {
            ...section,
            unsnappedHeight: section.height, // keep unsnapped height for resize drag
            height,
            minHeight,
            top
          }
        ]

        // ready for the top of the next section
        top += Math.max(height, minHeight)
        return entry
      })
    )

    return layout
  }
)

export const snapperSelector = createSelector(
  [sectionLayoutSelector, blocksSelector],
  (sections, blocks) => {
    if (!sections) return
    const tops = getSectionTops(sections)
    const layout = getBlockLayout(blocks, tops)
    return getSnapper(layout, sections)
  }
)

const getLayout = (block, sectionTop = 0) => {
  return new Rectangle({
    width: block.width,
    height: block.aspect ? block.width * block.aspect : block.height,
    left: block.column,
    // top is section-relative, convert to board-relative
    top: block.top + sectionTop
  })
}

export const dragLayoutSelector = createSelector(
  [
    dragOffsetSelector,
    selectedBlocksSelector,
    cropBlockSelector,
    sectionLayoutSelector
  ],
  (drag, selectedBlocks, cropBlock, sections) => {
    if (!drag || !drag.action || !selectedBlocks.length || !sections) return

    const tops = getSectionTops(sections)

    if (drag.action === 'move') {
      // move
      /////////

      let { x, y } = drag
      if (drag.shiftKey) {
        if (Math.abs(x) > Math.abs(y)) y = 0
        else x = 0
      }

      if (cropBlock) {
        const layout = getLayout(cropBlock, tops.get(cropBlock.section))

        const crop = cropBlock.image.crop || { top: 0, left: 0, scale: 1 }
        const blockHeight = cropBlock.width * cropBlock.aspect
        const contentWidth = cropBlock.width * crop.scale
        const contentHeight = contentWidth * cropBlock.image.aspect
        const maxTop = (contentHeight - blockHeight) / blockHeight
        const maxLeft = crop.scale - 1
        const newCrop = {
          top: clamp(crop.top - y / blockHeight, 0, maxTop),
          left: clamp(crop.left - x / cropBlock.width, 0, maxLeft),
          scale: crop.scale
        }

        return {
          layouts: {
            [cropBlock.id]: layout
          },
          crop: newCrop,
          action: 'crop-move'
        }
      }

      const layouts = {}
      selectedBlocks.forEach(block => {
        const layout = getLayout(block, tops.get(block.section))
        layout.x += x
        layout.y += y
        layouts[block.id] = layout
      })

      return { layouts, action: 'move', altKey: drag.altKey }
    } else if (drag.action.match(/^resize-/)) {
      // resize
      /////////

      const block = selectedBlocks[0]
      const layout = getLayout(block, tops.get(block.section))

      let newCrop = null
      if (cropBlock) {
        const crop = cropBlock.image.crop || { top: 0, left: 0, scale: 1 }
        const contentLayout = new Rectangle({
          top: layout.top - layout.height * crop.top,
          left: layout.left - layout.width * crop.left,
          width: layout.width * crop.scale,
          height: layout.width * crop.scale * cropBlock.image.aspect
        })
        resize(contentLayout, drag, cropBlock.image.aspect)

        // constrain
        // TODO: this better
        if (layout.top < contentLayout.top) contentLayout.top = layout.top
        if (layout.left < contentLayout.left) contentLayout.left = layout.left
        if (layout.right > contentLayout.right)
          contentLayout.right = layout.right
        // if(layout.bottom > contentLayout.bottom) contentLayout.bottom = layout.bottom

        newCrop = {
          top: (layout.top - contentLayout.top) / layout.height,
          left: (layout.left - contentLayout.left) / layout.width,
          scale: contentLayout.width / layout.width
        }
      } else {
        // const before = layout.width
        resize(layout, drag, block.aspect, block.text)
      }

      return {
        layouts: { [block.id]: layout },
        action: drag.action,
        crop: newCrop
      }
    } else if (drag.action.match(/^crop-frame-/)) {
      // Adjust Frame
      /////////

      const block = cropBlock
      const layout = getLayout(block, tops.get(block.section))
      const crop = block.image.crop || { top: 0, left: 0, scale: 1 }
      const contentLayout = new Rectangle({
        top: layout.top - layout.height * crop.top,
        left: layout.left - layout.width * crop.left,
        width: layout.width * crop.scale,
        height: layout.width * crop.scale * cropBlock.image.aspect
      })
      resize(layout, drag)

      if (layout.top < contentLayout.top) layout.top = contentLayout.top
      if (layout.left < contentLayout.left) layout.left = contentLayout.left
      if (layout.right > contentLayout.right) layout.right = contentLayout.right
      if (layout.bottom > contentLayout.bottom)
        layout.bottom = contentLayout.bottom

      const newCrop = {
        top: (layout.top - contentLayout.top) / layout.height,
        left: (layout.left - contentLayout.left) / layout.width,
        scale: contentLayout.width / layout.width
      }

      return {
        layouts: { [block.id]: layout },
        action: drag.action,
        crop: newCrop
      }
    }
  }
)

const resize = (layout, drag, aspect, text = false) => {
  // if block as no set aspect and shift key is held: use existing aspect
  if (!aspect && drag.shiftKey) aspect = layout.height / layout.width

  // west
  if (drag.action.match(/.?w$/)) {
    layout.left += drag.x
    if (drag.altKey) layout.right -= drag.x
    // east
  } else if (drag.action.match(/.?e$/)) {
    layout.right += drag.x
    if (drag.altKey) layout.left -= drag.x
  }
  // north
  if (drag.action.match(/n.?$/)) {
    if (aspect) {
      const currentBottom = layout.bottom
      layout.height = layout.width * aspect
      if (drag.altKey) layout.y += (currentBottom - layout.bottom) / 2
      else layout.y = currentBottom - layout.height
    } else if (!text) {
      layout.top += drag.y
      if (drag.altKey) layout.bottom -= drag.y
    }
    // south
  } else if (drag.action.match(/s.?$/)) {
    if (aspect) {
      const currentBottom = layout.bottom
      layout.height = layout.width * aspect
      if (drag.altKey) layout.y += (currentBottom - layout.bottom) / 2
    } else if (!text) {
      layout.bottom += drag.y
      if (drag.altKey) layout.top -= drag.y
    }
  }
}

export const dragSnappedSelector = createSelector(
  [dragLayoutSelector, snapperSelector],
  (dragLayout, snapper) => {
    if (!dragLayout) return

    if (dragLayout.action === 'move') {
      const layout = Rectangle.combine(Object.values(dragLayout.layouts))

      // passing all blocks as one to snap TODO: make it accept array and snap between
      return snapper({
        layout,
        // ID top stop a block snaping to its own guides. TODO allow multi-selections to snap
        id:
          Object.keys(dragLayout.layouts).length === 1
            ? Object.keys(dragLayout.layouts)[0]
            : null,
        action: 'move'
      })
    } else {
      return snapper({
        layout: Object.values(dragLayout.layouts)[0],
        action: dragLayout.action // e.g. 'resize-nw' (north west)
      })
    }
  }
)

/**
 * returns where the block should be placed
 * based on dragged size or click/hover position
 * and block aspect ratio
 * and snap to cols & sections
 */
export const placementSelector = createSelector(
  [
    placementPositionSelector,
    placementBlockSelector,
    placementWidthSelector,
    snapperSelector
  ],
  (placementPosition, placementBlock, placementWidth, snapper) => {
    if (!placementBlock || !placementPosition) return

    let layout
    let drag = placementPosition.rectangle
    let action
    // if placement by dragging
    if (drag) {
      layout = new Rectangle(drag).positive()

      // drag not big enough - treat as a click
      if (Math.abs(drag.width) < 0.3 && Math.abs(drag.height) < 0.3) {
        layout.width = placementWidth
        layout.height = placementBlock.text ? 0.8 : placementWidth
        action = 'place-click'
      } else {
        const northSouth = drag.height < 0 ? 'n' : 's'
        const eastWest = drag.width < 0 ? 'w' : 'e'
        action = `place-drag-${northSouth}${eastWest}`
      }
    } else {
      // placement by clicking
      layout = new Rectangle({
        top: placementPosition.y,
        left: placementPosition.x,
        width: placementWidth,
        height: placementBlock.text ? 0.8 : placementWidth
      })
      action = 'place-click'
    }

    return snapper({
      layout,
      aspect: placementBlock.aspect,
      action
    })
  }
)

/**
 * adjusts sections to hold current placement/dragged block if needed
 */
export const sectionLayoutAdjustedSelector = createSelector(
  [sectionLayoutSelector, placementSelector, dragSnappedSelector],
  (sections, placement, draggedBlocks) => {
    // Do nothing if not placing/dragging
    if (!placement && !draggedBlocks) return sections

    const { top, bottom } =
      (placement && placement.layout) || draggedBlocks.layout

    let expand
    let push
    const sectionLayout = new Map()
    sections.forEach(section => {
      const sectionHeight = Math.max(section.height, section.minHeight)
      const sectionBottom = section.top + sectionHeight

      // if a section was expanded push following sections down
      if (push) {
        sectionLayout.set(section.id, {
          ...section,
          top: section.top + push
        })
      } else if (
        // if placement top is in this section
        section.top <= top &&
        (sectionBottom > top ||
          // below bottom of last section counts as being within
          section.number === sections.size - 1) &&
        // and bottom is overhanging
        bottom + 1 > sectionBottom
      ) {
        // expand section
        expand = bottom + 1 - sectionBottom
        sectionLayout.set(section.id, {
          ...section,
          minHeight: sectionHeight + expand
        })
        // from now we will be pushing sections down
        push = expand
      } else {
        sectionLayout.set(section.id, section)
      }
    })

    return sectionLayout
  }
)

/**
 * @param {Map} sections with layout
 * @returns {Map} section tops
 */
const getSectionTops = sections =>
  sections &&
  new Map([...sections.values()].map(section => [section.id, section.top]))
/**
 * select top of each section
 * Some things only need to know this and using sectionLayoutSelector
 * would cause things to recompute for irrelivant changes
 * TODO; check equality where used
 */
export const sectionTopsSelector = createSelector(
  [sectionLayoutAdjustedSelector],
  getSectionTops
)

/**
 * @param {Object} blocks - blocks with layout
 * @param {Map} sectionTops - tops of sections
 * @returns {Object} blocks with layout computed relative to board
 */
const getBlockLayout = (blocks, sectionTops) => {
  if (!sectionTops) return
  const blockLayout = {}
  if (blocks) {
    Object.values(blocks).forEach(block => {
      // ignore blocks without a place on the board
      if (block.column === undefined) return
      blockLayout[block.id] = getLayout(block, sectionTops.get(block.section))
    })
  }
  return blockLayout
}
export const blockLayoutSelector = createSelector(
  [blocksSelector, sectionTopsSelector],
  getBlockLayout
)
export const orderedBlockLayoutSelector = createSelector(
  [blocksSelector, sectionTopsSelector],
  (blocks, sectionTops) => {
    if (!blocks || !sectionTops) return
    return getBlockLayout(
      Object.values(blocks || []).sort(
        (a, b) => (a.stackingOrder || 0) - (b.stackingOrder || 0)
      ),
      sectionTops
    )
  }
)

export const currentBlockSizeSelector = createSelector(
  [currentBlockSelector, boardSizeSelector],
  (block, boardSize) => {
    if (!isLoaded(block)) return

    let width
    let height
    const boardAspect = (boardSize.height - 60) / (boardSize.width - 60)
    const blockAspect =
      block.aspect ||
      block.image.aspect ||
      block.image.height / block.image.width
    if (blockAspect < boardAspect) {
      width = boardSize.width - 60
      height = width * blockAspect
    } else {
      height = boardSize.height - 60
      width = height / blockAspect
    }

    const crop =
      block.image && block.image.crop
        ? block.image.crop
        : { top: 0, left: 0, scale: 1 }

    const contentWidth = width * crop.scale
    if (block.image && contentWidth > block.image.width) {
      width = block.image.width / crop.scale
      height = width * blockAspect
    }

    const contentHeight =
      width * crop.scale * (block.image ? block.image.aspect : block.aspect)
    if (block.image && contentHeight > block.image.height) {
      const maxContentWidth = block.image.height / block.image.aspect
      width = maxContentWidth / crop.scale
      height = width * blockAspect
    }

    return { width, height }
  }
)

export const orderedBlockIdsSelector = createSelector(
  [blockLayoutSelector, blocksSelector],
  (blockLayout, blocks) => {
    const orderedBlocks = []
    if (blockLayout) {
      Object.keys(blockLayout).forEach(id => {
        if (blocks[id].image)
          orderedBlocks.push({ id, top: blockLayout[id].top })
      })
    }
    return orderedBlocks
      .sort((a, b) => {
        const top = a.top - b.top
        if (top !== 0) return top
        return a.left - b.left
      })
      .map(({ id }) => id)
  }
)

/**
 * selects groups of blocks, with layout computed
 */
export const blockGroupsSelector = createSelector(
  [
    blocksSelector,
    blockLayoutSelector,
    _selectedIdsSelector,
    dragLayoutSelector
  ],
  (blocks, blockLayout, selectedBlocks, dragLayout) => {
    const blockGroups = {}

    if (blocks) {
      function addToGroup(block) {
        // ignore blocks without a place on the board
        if (block.column === undefined) return

        // if currently dragging or resizing, move block
        if (dragLayout && dragLayout.layouts[block.id]) {
          block.layout = dragLayout.layouts[block.id]
          if (dragLayout.crop) {
            block.image.cropOverride = dragLayout.crop
          }
        } else {
          if (block.image) block.image.cropOverride = null
          block.layout = blockLayout[block.cloneOf || block.id]
        }

        let group
        // if multiple blocks are selected
        // including this block of this block's group
        if (
          selectedBlocks.length > 1 &&
          selectedBlocks.includes(block.group || block.id)
        ) {
          group = 'selected' // block is in the special 'selected' group
        } else {
          group = block.group || block.id // if no group, use block id to make its own group
        }

        if (group in blockGroups) {
          // update groups stacking order to match highest block
          blockGroups[group].stackingOrder = Math.max(
            blockGroups[group].stackingOrder,
            block.stackingOrder
          )
          blockGroups[group].blocks.push(block)
        } else {
          blockGroups[group] = {
            id: group,
            stackingOrder: block.stackingOrder,
            blocks: [block]
          }
        }
      }

      Object.values(blocks).forEach(addToGroup)

      if (dragLayout && dragLayout.action === 'move' && dragLayout.altKey) {
        Object.keys(dragLayout.layouts).forEach(blockId => {
          const block = blocks[blockId]
          const clone = {
            ...block,
            cloneOf: block.id,
            stackingOrder: block.stackingOrder - 1,
            id: block.id + '-orig'
          }
          if (block.group) clone.group = block.group + '-orig'
          addToGroup(clone)
        })
      }
    }

    // set the layout of each group to be the combination of its blocks
    Object.keys(blockGroups).forEach(group => {
      blockGroups[group].layout = Rectangle.combine(
        blockGroups[group].blocks.map(block => block.layout)
      )
    })

    return blockGroups
  }
)
